import { useEffect, useMemo, useState } from 'react'
import { useSetRecoilState, useRecoilValue } from 'recoil'
import { getImageDimensions } from 'utils'
import {
  itemSelector,
  caseAtom,
  selectedItemAtom,
  itemAtomFamily,
  showPinturaAtom,
  backgroundImageAtom,
  pageThemeOverrideSelector,
  pageLayoutAtom,
  pageLoadingAtom,
  themeOverrideAtom,
} from 'atoms'
import { caseImagesAtom, filledImagesSelector } from '@/atoms/caseImageAtoms'
import { syncImageEditToEditorPages } from '@/utils/editor/syncImageEditToEditorPages'
import { resolveCaseImageForItem } from '@/utils/editor/resolveCaseImageForItem'
import { applySavedEditsToCutout } from '@/utils/editor/applySavedEditsToCutout'
import { createCutoutImageReader } from '@/utils/editor/cutoutImageReader'
import { useUpdatePageItems } from '@/hooks/editor/useUpdatePageItems'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import getUrlPath from 'utils/global/getUrlPath'
import { isEqual } from 'lodash'
import { isImagePlaceholder } from 'utils/editor/imagePlaceholder'
import { useSetToast } from 'hooks'
import { EventCategory, logger } from 'utils/logger'
import { sanitizeCaseImageUrl } from '@/utils/sanitizers/sanitizeCaseImageUrl'
import { sanitizeImageUrl } from '@/utils/sanitizers/sanitizeImageUrl'
import { useTranslation } from 'react-i18next'
import {
  createDefaultImageReader,
  createDefaultImageWriter,
} from '@messenger/pintura'
import { uploadEditedBackgroundImage } from '@/api/customThemes'
import { deleteEditedBackgroundImage } from '@/api/customThemes'
import { getPresignedUploadUrl } from '@/api/caseImages'
import { uploadToPresignedUrl } from '@messenger/utils'

export const usePintura = (_, editBackground) => {
  const handleError = useErrorHandler('usePintura')
  const setToast = useSetToast()
  const { t } = useTranslation()
  const _case = useRecoilValue(caseAtom)
  const selectedItem = useRecoilValue(selectedItemAtom)
  const item = useRecoilValue(itemAtomFamily(selectedItem))
  const backgroundImage = useRecoilValue(backgroundImageAtom)
  const setShowPintura = useSetRecoilState(showPinturaAtom)
  const setLoadingPage = useSetRecoilState(pageLoadingAtom)
  const themeOverride = useRecoilValue(themeOverrideAtom)

  // Use useUpdatePageItems to get synchronous items sync for immediate updates
  const { setEditorPagesItems, syncItemsToEditorPages } = useUpdatePageItems()

  // case image
  const setEditedImageURL = useSetRecoilState(
    itemSelector({ id: item.id, path: 'image.editedUrl' }),
  )
  const setImageURL = useSetRecoilState(
    itemSelector({ id: item.id, path: 'image.url' }),
  )
  const setImageEdits = useSetRecoilState(
    itemSelector({ id: item.id, path: 'image.edits' }),
  )
  // Provenance of an applied AI cutout (see ImageItem.image.aiVariantId).
  const setAiVariantId = useSetRecoilState(
    itemSelector({ id: item.id, path: 'image.aiVariantId' }),
  )
  const setAiVariantUrl = useSetRecoilState(
    itemSelector({ id: item.id, path: 'image.aiVariantUrl' }),
  )
  // Set by "Use original photo" in the Remove Background tool: for the rest
  // of this Pintura session the editor shows the original photo instead of
  // the item's cutout, so an export from it brings the background back.
  const [showingOriginal, setShowingOriginal] = useState(false)

  const [originalImageSize, setOriginalImageSize] = useState(null)

  const { id: caseId } = _case || {}
  const filledImages = useRecoilValue(filledImagesSelector)
  const caseImages = useRecoilValue(caseImagesAtom)
  const {
    image: { url, edits, aiVariantId, aiVariantUrl } = {},
    properties: { caseImage } = {},
  } = item || {}
  const photoUrl = sanitizeImageUrl(url)

  // The accepted cutout the item shows, if any. While it does, Pintura edits
  // the whole stored cutout with the item's saved edits instead of the
  // original photo, so a later crop, turn, flip or filter applies to the
  // cutout and the export keeps the cutout and its "Edited with AI" tag.
  const cutout =
    !editBackground && !showingOriginal && aiVariantId && aiVariantUrl
      ? { variantId: aiVariantId, url: aiVariantUrl }
      : null
  const cutoutUrl = cutout?.url
  // The cutout is read at the photo's size, where the saved edits were
  // measured, and written as a PNG to keep its transparency.
  const imageReader = useMemo(
    () =>
      cutoutUrl
        ? createCutoutImageReader(photoUrl)
        : createDefaultImageReader(),
    [cutoutUrl, photoUrl],
  )
  const imageWriter = useMemo(
    () =>
      cutoutUrl
        ? createDefaultImageWriter({ mimeType: 'image/png' })
        : createDefaultImageWriter(),
    [cutoutUrl],
  )

  // The CaseImage row behind this item, needed to request AI alterations.
  // Null for placeholders, logos and design assets, which hides the tool.
  const caseImageId = resolveCaseImageForItem(caseImages, item)?.id ?? null

  // background image
  const pageThemeOverride = useRecoilValue(pageLayoutAtom)
  const setEditedBackground = useSetRecoilState(
    pageThemeOverrideSelector({ path: 'background_edits' }),
  )

  useEffect(() => {
    const isPlaceHolderURL = isImagePlaceholder(url)
    if (!isPlaceHolderURL) return
    const caseImageIdentifier = caseImage || 0
    const sanitizedImageURL = sanitizeCaseImageUrl(
      filledImages,
      caseImageIdentifier,
    )
    if (sanitizedImageURL) {
      setImageURL(sanitizedImageURL)
    }
  }, [url, caseImage, filledImages, setImageURL])

  const getOrignalImageSize = async (props) => {
    const { item } = props

    const isItemImage = item?.image
    if (!isItemImage) return

    const originalSizes = await getImageDimensions(item?.image?.url)
    if (isEqual(originalSizes, originalImageSize)) return
    setOriginalImageSize(originalSizes)
  }

  useEffect(() => {
    const notAnImage = item?.type && item?.type !== 'image'
    const noItem = !item || Object.keys(item).length === 0
    const noBackground = !editBackground
    const doNotSetOriginalImageSize = notAnImage || noItem || noBackground

    if (doNotSetOriginalImageSize) return

    const sizeProps = {
      item,
      pageThemeOverride,
      themeOverride,
      editBackground,
    }

    getOrignalImageSize(sizeProps)
    // eslint-disable-next-line
  }, [item, editBackground, pageThemeOverride, themeOverride])

  // methods
  const handleCaseImageDelete = async () => {
    // No-op: orphaned S3 edits are cleaned up by bucket lifecycle rules.
  }

  const handleBackgroundImageDelete = async () => {
    if (pageThemeOverride?.background_edits?.url) {
      try {
        const editedUrlPath = getUrlPath(
          pageThemeOverride?.background_edits?.url,
        )
        await deleteEditedBackgroundImage(caseId, {
          filename: editedUrlPath,
        })
      } catch (error) {
        handleError(error?.response?.data?.message || error.message, error)
      }
    }
  }

  const handleCaseImageUpload = async (blob) => {
    try {
      const contentType = blob.type || 'image/png'
      const { url: presignedUrl, s3Key } = await getPresignedUploadUrl(
        caseId,
        contentType,
      )
      const file = new File([blob], 'pintura-edit', { type: contentType })
      await uploadToPresignedUrl(presignedUrl, file)
      const bucketUrl = (
        process.env.REACT_APP_S3BUCKET_CASE_IMAGES || ''
      ).replace(/\/+$/, '')
      if (!bucketUrl) {
        throw new Error('REACT_APP_S3BUCKET_CASE_IMAGES is not configured')
      }
      const location = `${bucketUrl}/${s3Key}`
      return { status: 200, data: { location } }
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
      return { status: 500, data: null }
    }
  }

  const handleProcessing = () => {
    if (editBackground) {
      setLoadingPage(true)
      setShowPintura(false)
    }
  }

  const handleEditedBackgroundImageUpload = async (formData) => {
    try {
      const newBackground = await uploadEditedBackgroundImage(caseId, formData)
      setLoadingPage(false)
      return newBackground
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const handleCaseImageUploadSuccess = (imageState, data) => {
    const editedImageUrlWithCacheBust = `${data.location}?t=${Date.now()}`

    // An export from the cutout is still the cutout, so it keeps the AI
    // provenance; an export from the original photo clears it.
    syncImageEditToEditorPages({
      editedUrl: editedImageUrlWithCacheBust,
      imageEdits: imageState,
      aiVariantId: cutout?.variantId,
      aiVariantUrl: cutout?.url,
      setEditorPagesItems,
      setEditedImageUrl: setEditedImageURL,
      setImageEdits,
      setAiVariantId,
      setAiVariantUrl,
      syncItemsToEditorPages,
    })
    return true
  }

  /**
   * Put an accepted AI cutout on the item. It goes into `editedUrl` (the
   * same slot a Pintura export uses, which outranks every other image URL
   * when the page renders), and its variant id and the whole stored cutout
   * are kept, so Pintura opens the cutout from then on. The source `url` and
   * Pintura `edits` stay untouched, so "Use original photo" can still bring
   * the original back. The cutout is generated from the unedited CaseImage
   * source, so the item's saved Pintura edits (crop, turn, flips, filter,
   * adjustments, markup) are applied to it and that copy uploaded before it
   * goes on the item; the stored cutout itself stays whole, so it can be
   * re-applied for free. If the edits cannot be applied the item is left as
   * it was and the error shown; the cutout stays accepted, so opening the
   * tool again re-applies it free. Resolves true once the item holds the
   * cutout and false when it does not, so the tool says "accepted" only for
   * a cutout that is on the item.
   */
  const applyAiVariant = async ({ variantId, url: variantUrl }) => {
    let editedUrl
    try {
      editedUrl = await applySavedEditsToCutout({
        cutoutUrl: variantUrl,
        photoUrl,
        edits,
        upload: async (file) => {
          // handleCaseImageUpload reports its own failure.
          const result = await handleCaseImageUpload(file)
          return result?.status === 200 && result.data
            ? `${result.data.location}?t=${Date.now()}`
            : null
        },
      })
    } catch (error) {
      handleError(error?.message, error)
      return false
    }
    if (!editedUrl) return false
    syncImageEditToEditorPages({
      editedUrl,
      imageEdits: edits ?? {},
      aiVariantId: variantId,
      aiVariantUrl: variantUrl,
      setEditorPagesItems,
      setEditedImageUrl: setEditedImageURL,
      setImageEdits,
      setAiVariantId,
      setAiVariantUrl,
      syncItemsToEditorPages,
    })
    // The new cutout is what the editor shows now, even after "Use original
    // photo" earlier in this session.
    setShowingOriginal(false)
    return true
  }

  /**
   * "Use original photo": Pintura goes back to the original photo with the
   * item's saved edits. Nothing is written until the person exports, which
   * then replaces the cutout and clears its provenance; closing Pintura
   * instead keeps the cutout.
   */
  const showOriginalPhoto = () => setShowingOriginal(true)

  const handleEditedBackgroundImageUploadSuccess = (imageState, data) => {
    setEditedBackground({
      url: data,
      edits: imageState,
      theme_id: themeOverride?.theme_id,
    })
  }

  const handleDoneEditing = async (props) => {
    const { imageState, dest } = props

    // if there is already an edited image url, then delete
    if (editBackground) {
      handleBackgroundImageDelete()
    } else {
      handleCaseImageDelete()
    }

    if (editBackground) {
      const formData = new FormData()
      formData.append('file', dest)
      const result = await handleEditedBackgroundImageUpload(formData)
      if (result?.data && result?.status === 200) {
        handleEditedBackgroundImageUploadSuccess(imageState, result.data)
        setShowPintura(false)
      }
    } else {
      const result = await handleCaseImageUpload(dest)
      if (result?.data && result?.status === 200) {
        handleCaseImageUploadSuccess(imageState, result.data)
        setShowPintura(false)
      }
    }
  }

  const handleFailure = (errorType) => {
    const errorMessage =
      errorType === 'process'
        ? t('toast.errorProcessingRequest')
        : t('toast.errorInitializingEditor')

    setToast({
      text: errorMessage,
    })
    logger.error({
      message:
        errorType === 'process'
          ? 'Error processing image in Pintura'
          : 'Error initializing editor in Pintura',
      category: EventCategory.ERROR,
      error: new Error(errorMessage),
      data: {
        errorType,
        caseId,
        itemId: item?.id,
        imageUrl: editBackground
          ? `${process.env.REACT_APP_S3BUCKET_THEME_IMAGES}${backgroundImage}`
          : (cutoutUrl ?? photoUrl),
        rawItemUrl: url,
        editBackground,
      },
    })
    setLoadingPage(false)
    setShowPintura(false)
  }

  const caseImageEdits = {
    ...edits,
    targetSize: {
      width: originalImageSize?.width,
      height: originalImageSize?.height,
    },
  }

  const imageUrl = editBackground
    ? `${process.env.REACT_APP_S3BUCKET_THEME_IMAGES}${backgroundImage}`
    : photoUrl

  return {
    // The photo the item is made from; the Remove Background tool cuts it out.
    url: imageUrl,
    // What Pintura opens: the item's cutout while it shows one, else the photo.
    src: cutoutUrl ?? imageUrl,
    imageReader,
    imageWriter,
    edits: editBackground
      ? pageThemeOverride?.background_edits?.edits
      : caseImageEdits,
    handleFailure,
    handleProcessing,
    handleDoneEditing,
    caseImageId,
    applyAiVariant,
    appliedCutout: cutout,
    showOriginalPhoto,
  }
}

export default usePintura
